import React, { lazy } from 'react';
import { useLocation } from 'react-router-dom';
import { portalForHostname, isPortalOperatorPage } from '../utils/portalExperience';
import { getStoredUser } from '../utils/featureAccess';
import { PortalAccessPage, PortalPublicPage, PortalSupportPage, PortalUnavailablePage } from './PortalPublicPage';

const PortalLegal = lazy(() => import('../pages/PortalLegal'));
const SELF_SIGNUP_PATHS = new Set(['/signup', '/agency-signup', '/enterprise-signup', '/partner-signup', '/client-signup', '/independent-agent-signup', '/partner-enterprise-signup']);
const ACCOUNT_PATHS = new Set(['/account-invite', '/set-password', '/agency-agent-invite', '/partner-consultant-invite']);

/** Keep platform marketing out of the portal without changing API authorisation. */
export function PortalPublicBoundary({ children }) {
    const location = useLocation();
    if (!portalForHostname()) return children;
    // React Router matches public routes case-insensitively and decodes segments.
    // Apply the same aliases here so they cannot expose the platform marketing.
    let path = location.pathname;
    try { path = decodeURIComponent(path); } catch (_) { /* Keep malformed paths for the not-found route. */ }
    path = path.toLowerCase().replace(/\/+$/, '') || '/';
    const params = new URLSearchParams(location.search);
    if (path === '/help' || path === '/contact') return <PortalSupportPage />;
    if (path === '/privacy' || path === '/legal') return <PortalLegal kind={path === '/privacy' ? 'privacy' : 'terms'} />;
    if (['/about', '/careers', '/pricing', '/our-services'].includes(path)
        || isPortalOperatorPage(path)
        || path.startsWith('/templates/preview')) return <PortalUnavailablePage />;
    const invitedSignup = (path === '/agency-signup' && params.get('enterpriseInviteToken')?.trim())
        || (path === '/partner-signup' && params.get('partnerEnterpriseInviteToken')?.trim());
    if (SELF_SIGNUP_PATHS.has(path) && !invitedSignup) return <PortalAccessPage />;
    const standaloneWorkflow = ['/signed', '/registration/success'].includes(path)
        || /^\/(consent|viewing|meet|track|client-form|news|profile)\//.test(path);
    const signedOut = !getStoredUser()?.token;
    const signedOutBrowse = signedOut && (['/collection', '/new-developments', '/rental-application'].includes(path) || path.startsWith('/property/'));
    if (invitedSignup || ACCOUNT_PATHS.has(path) || standaloneWorkflow || signedOutBrowse
        || (signedOut && ['/enterprise/invites', '/partner-enterprise/invites'].includes(path) && params.get('token'))) {
        return <PortalPublicPage embedded>{children}</PortalPublicPage>;
    }
    return children;
}

export function PortalPublicNotFound() {
    return portalForHostname() ? <PortalUnavailablePage /> : null;
}
